import { useEffect } from "react";

import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { captureEvent } from "@/lib/analyticsQueue";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { useCommunityCta } from "./useCommunityCta";
import { WhatsAppIcon } from "./WhatsAppIcon";
import type { CommunityCtaSource } from "./constants";

interface WhatsAppCommunityCardProps {
  source: CommunityCtaSource;
  /** Called after the join link is clicked (e.g. to dismiss a host modal). */
  onJoin?: () => void;
  className?: string;
}

/**
 * Reusable WhatsApp community invite card — green (#25D366) framing matching the
 * existing retention CTA. Reused on the payment-success page, profile page, and
 * inside the first-generation modal. Fires `community_cta_view` on mount.
 */
export function WhatsAppCommunityCard({
  source,
  onJoin,
  className,
}: WhatsAppCommunityCardProps) {
  const { t } = useLanguage();
  const { getCommunityHref, openCommunity } = useCommunityCta();

  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.COMMUNITY_CTA_VIEW, { source });
  }, [source]);

  return (
    <div
      className={cn(
        "rounded-2xl border border-[#25D366]/30 bg-[#25D366]/10 p-5 text-start",
        className,
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="rounded-full bg-[#25D366] p-2">
          <WhatsAppIcon className="w-4 h-4 text-white" />
        </div>
        <h3 className="font-bold text-foreground">{t.community.title}</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4 leading-snug">
        {t.community.description}
      </p>
      <a
        href={getCommunityHref()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          openCommunity(source);
          onJoin?.();
        }}
        className="flex items-center justify-center gap-2 w-full rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1fba59]"
      >
        <WhatsAppIcon className="w-4 h-4" />
        {t.community.joinButton}
      </a>
    </div>
  );
}
